import logger from '../../Utils/logger';
/**
 * Members reducer for guest management.
 * Handles all guest mutations: add, remove, update services, reset.
 * No flushSync needed; reducer guarantees atomic updates.
 */

import {
  createInitialGuests,
  normalizeMembersList,
  reindexGuestLabels,
  nextGuestId,
  PRIMARY_GUEST_ID,
} from '../../Utils/guestMembers';
import { MAX_GUESTS, resolveMaxServiceCount } from '../../Utils/bookingConfig';

export const GuestsActionTypes = {
  ADD_GUEST: 'ADD_GUEST',
  REMOVE_GUEST: 'REMOVE_GUEST',
  SET_ACTIVE_GUEST: 'SET_ACTIVE_GUEST',
  UPDATE_MEMBER: 'UPDATE_MEMBER',
  UPDATE_MEMBER_SERVICES: 'UPDATE_MEMBER_SERVICES',
  ADD_SERVICE_TO_GUEST: 'ADD_SERVICE_TO_GUEST',
  REMOVE_SERVICE_FROM_GUEST: 'REMOVE_SERVICE_FROM_GUEST',
  RESET_MEMBERS: 'RESET_MEMBERS',
  NORMALIZE_MEMBERS: 'NORMALIZE_MEMBERS',
  FILTER_TO_SINGLE_BOOKING: 'FILTER_TO_SINGLE_BOOKING',
};

/**
 * Guest members reducer.
 * All actions return a normalized, reindexed members array.
 */
export const guestsReducer = (state, action) => {
  switch (action.type) {
    case GuestsActionTypes.ADD_GUEST: {
      if (state.length >= MAX_GUESTS) {
        logger.warn(`Cannot add guest; max limit (${MAX_GUESTS}) reached`);
        return state;
      }
      const newId = nextGuestId(state);
      const newGuest = {
        id: newId,
        name: '',
        displayName: '',
        customerName: '',
        isPrimary: false,
        services: [],
      };
      return normalizeMembersList([...state, newGuest]);
    }

    case GuestsActionTypes.REMOVE_GUEST: {
      const guestId = action.payload;
      if (guestId === PRIMARY_GUEST_ID) {
        logger.warn('Cannot remove primary guest');
        return state;
      }
      const filtered = state.filter((m) => m.id !== guestId);
      if (filtered.length === state.length) {
        logger.warn(`Guest ${guestId} not found`);
        return state;
      }
      const relabeled = reindexGuestLabels(
        filtered.map((m, i) => ({ ...m, isPrimary: i === 0 }))
      );
      return normalizeMembersList(relabeled);
    }

    case GuestsActionTypes.UPDATE_MEMBER: {
      const { memberId, updates } = action.payload;
      return state.map((m) =>
        m.id === memberId ? { ...m, ...updates } : m
      );
    }

    case GuestsActionTypes.UPDATE_MEMBER_SERVICES: {
      const { memberId, services } = action.payload;
      return state.map((m) =>
        m.id === memberId ? { ...m, services: services || [] } : m
      );
    }

    case GuestsActionTypes.ADD_SERVICE_TO_GUEST: {
      const { memberId, service } = action.payload;
      const maxServiceCount = resolveMaxServiceCount(action.payload.maxServiceCount);
      return state.map((m) => {
        if (m.id !== memberId) return m;
        if ((m.services?.length || 0) >= maxServiceCount) {
          logger.warn(
            `Cannot add service to guest ${memberId}; max limit (${maxServiceCount}) reached`
          );
          return m;
        }
        return { ...m, services: [...(m.services || []), service] };
      });
    }

    case GuestsActionTypes.REMOVE_SERVICE_FROM_GUEST: {
      const { memberId, serviceId, count } = action.payload;
      return state.map((m) => {
        if (m.id !== memberId) return m;
        return {
          ...m,
          services: (m.services || []).filter(
            (s) => !(s.id === serviceId && s.count === count)
          ),
        };
      });
    }

    case GuestsActionTypes.RESET_MEMBERS:
      return createInitialGuests();

    case GuestsActionTypes.NORMALIZE_MEMBERS:
      return normalizeMembersList(action.payload);

    case GuestsActionTypes.FILTER_TO_SINGLE_BOOKING: {
      // Single booking mode: keep only primary guest with services.
      const primary = state.find((m) => m.isPrimary) || state[0];
      if (!primary) return createInitialGuests();
      return normalizeMembersList([
        { ...primary, isPrimary: true, services: primary.services || [] },
      ]);
    }

    default:
      logger.warn(`Unknown action type: ${action.type}`);
      return state;
  }
};

/**
 * Action creator helpers for cleaner usage.
 */
export const guestActions = {
  addGuest: () => ({ type: GuestsActionTypes.ADD_GUEST }),
  removeGuest: (guestId) => ({ type: GuestsActionTypes.REMOVE_GUEST, payload: guestId }),
  updateMember: (memberId, updates) => ({
    type: GuestsActionTypes.UPDATE_MEMBER,
    payload: { memberId, updates },
  }),
  updateMemberServices: (memberId, services) => ({
    type: GuestsActionTypes.UPDATE_MEMBER_SERVICES,
    payload: { memberId, services },
  }),
  addServiceToGuest: (memberId, service, maxServiceCount) => ({
    type: GuestsActionTypes.ADD_SERVICE_TO_GUEST,
    payload: { memberId, service, maxServiceCount },
  }),
  removeServiceFromGuest: (memberId, serviceId, count) => ({
    type: GuestsActionTypes.REMOVE_SERVICE_FROM_GUEST,
    payload: { memberId, serviceId, count },
  }),
  resetMembers: () => ({ type: GuestsActionTypes.RESET_MEMBERS }),
  normalizeMembers: (members) => ({
    type: GuestsActionTypes.NORMALIZE_MEMBERS,
    payload: members,
  }),
  filterToSingleBooking: () => ({
    type: GuestsActionTypes.FILTER_TO_SINGLE_BOOKING,
  }),
};
